import { MutableRefObject, ReactNode, useLayoutEffect, useRef } from "react";

import { Popover } from "antd";
import styled from "styled-components";

type PositionT = {
  x: number;
  y: number;
  /**
   * A box to place against, drawn from (x, y) as its top-left corner. Without
   * one the trigger is a 20px square centred on x.
   */
  width?: number;
  height?: number;
};

type ContextMenuT = {
  allow?: boolean;
  content: ReactNode;
  position?: PositionT | null;
  visible?: boolean | null;
  className?: string;
  /**
   * A class on the floating overlay itself, for callers that need to style the
   * popover's surface. `className` cannot do it: antd v4 puts that on the
   * trigger element, not on the overlay.
   */
  overlayClassName?: string;
  placement?: "left" | "right" | "top" | "bottom";
  zIndex?: number;
};

/**
 * The overlay is placed against the trigger ONCE, when it opens. Moving or
 * resizing the trigger afterwards - all a new `position` does - leaves the
 * overlay where it was: the menu swaps its content but stays over the last
 * feature the user clicked. antd v4 keeps the alignment it computed, and re-runs
 * it only for a window resize or a scroll, so a moved trigger has to say so.
 */
function useRealign(
  ref: MutableRefObject<Maybe<{ forcePopupAlign?: () => void }>>,
  open: boolean,
  position: Maybe<PositionT>
): void {
  // Layout effect: the trigger's new offsets are in the DOM by now, and the
  // overlay moves in the same frame rather than being painted once at the old
  // spot and corrected on the next one.
  useLayoutEffect(() => {
    if (!open) return;
    ref.current?.forcePopupAlign?.();
  }, [ref, open, position?.x, position?.y, position?.width, position?.height]);
}

function ContextMenu({
  allow,
  content,
  position = null,
  visible = null,
  className,
  overlayClassName,
  placement = "right",
  zIndex
}: ContextMenuT) {
  const popoverRef = useRef<Maybe<{ forcePopupAlign?: () => void }>>(null);
  useRealign(popoverRef, !!allow && !!position, position);

  if (!allow || !position) return null;

  return (
    <Popover
      ref={popoverRef}
      content={<ContentWrapper>{content}</ContentWrapper>}
      placement={placement}
      className={className}
      overlayClassName={overlayClassName}
      zIndex={zIndex}
      trigger=""
      data-testid="context-menu"
      open={visible && allow}>
      {/* invisible trigger to position contextmenu */}
      <Trigger
        x={position.x}
        y={position.y}
        $width={position.width}
        $height={position.height}
      />
    </Popover>
  );
}

export default ContextMenu;

const ContentWrapper = styled.div`
  display: grid;
  margin: -12px -16px;
`;

const Trigger = styled.div<{ x: number; y: number; $width?: number; $height?: number }>`
  position: absolute;
  top: ${(props) => props.y}px;
  left: ${(props) => props.x}px;
  width: ${(props) => props.$width ?? 20}px;
  height: ${(props) => props.$height ?? 20}px;
  background-color: red;
  transform: ${(props) => (props.$width === undefined ? "translateX(-50%)" : "none")};
  visibility: hidden;
  z-index: 100;
`;
